import { Divider } from '@material-ui/core';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import * as qs from 'qs';
import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import WindsorApi from '../api/WindsorApi';
import LinkRouter from '../common/LinkRouter';
import LoadingIndicator from '../common/LoadingIndicator';
import MobxContext from '../mobx/MobxContext';
import Executions from './executions/Executions';
import FamilySettings from './FamilySettings';
import Tests from './tests/Tests';
import Workflows from './workflows/Workflows';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 1200,
      margin: 'auto'
    },
    header: {
      marginBottom: 16,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }
    },
    tabs: {
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: darken(theme.palette.background.default, 0.03)
    },
    divider: {
      marginBottom: 16,
      marginTop: 8
    },
    breadcrumb: {
      marginBottom: 16
    },
    tabContent: {
      paddingTop: 16
    },
    familyDescription: {
      marginBottom: 16,
      [theme.breakpoints.up('md')]: {
        marginBottom: 0
      }
    }
  })
);

const windsorApi = new WindsorApi();

const TabContent = ({ display, children, className }) => <div style={{ display: display ? 'block' : 'none' }} className={className}>{children}</div>;

function FamilyPage(props: RouteComponentProps) {
  const { match: { params: { familyId } }, history, location } = props;
  const { tab = 'workflows' } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const classes = useStyles({});
  const [family, setFamily] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const { preferenceStore } = useContext(MobxContext);

  useEffect(() => {
    setFamily(null);
    windsorApi.getFamily(familyId).then(family => {
      // Add recently viewed to preferences
      preferenceStore.addRecentlyViewedFamily(family.GUID, family.Name, family.Description);
      setFamily(family);
    });
  }, [familyId, preferenceStore]);

  const onTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    history.push({ search: `?tab=${newValue}` });
  };

  const onNewWorkflow = () => {
    history.push(`/families/${familyId}/workflows/new`);
  };

  const onEditFamily = () => {
    onMenuClose();
    history.push(`/families/${familyId}/edit`);
  };

  const onLoadWorkflowFromFile = () => {
    onMenuClose();
    history.push(`/families/${familyId}/workflows/new?loadFromFile=true`);
  };

  const onOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const onMenuClose = () => {
    setMenuAnchorEl(null);
  };

  return family ? (
    <div className={classes.root}>
      <Breadcrumbs aria-label="breadcrumb">
        <LinkRouter color="textSecondary" to="/families">
          Families
        </LinkRouter>
        <Typography color="textPrimary">{family.Name}</Typography>
      </Breadcrumbs>
      <Divider className={classes.divider} light />
      <div className={classes.header}>
        <Typography variant="h6" className={classes.familyDescription}>{family.Description || family.Name}</Typography>
        <div>
          <ButtonGroup variant="contained" color="primary" aria-label="split button">
            <Button onClick={onNewWorkflow}>New Workflow</Button>
            <Button
              color="primary"
              size="small"
              aria-label="more options menu"
              aria-haspopup="menu"
              onClick={onOpenMenu}
            >
              <ArrowDropDownIcon fontSize="small" />
            </Button>
          </ButtonGroup>
          <Menu
            id="more-options-menu"
            anchorEl={menuAnchorEl}
            keepMounted
            open={Boolean(menuAnchorEl)}
            onClose={onMenuClose}
          >
            <MenuItem onClick={onEditFamily}>
              Edit Family Settings
            </MenuItem>
            <MenuItem onClick={onLoadWorkflowFromFile}>
              Load Workflow From File
            </MenuItem>
          </Menu>
        </div>
      </div>

      <Tabs
        className={classes.tabs}
        value={tab}
        onChange={onTabChange}
        aria-label="Family Tabs"
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="Workflows" value="workflows" />
        <Tab label="Executions" value="executions" />
        <Tab label="Tests" value="tests" />
        <Tab label="Settings" value="settings" />
      </Tabs>
      <TabContent display={tab === 'workflows'} className={classes.tabContent}>
        <Workflows familyId={familyId} />
      </TabContent>
      <TabContent display={tab === 'executions'} className={classes.tabContent}>
        <Executions familyId={familyId} />
      </TabContent>
      <TabContent display={tab === 'tests'} className={classes.tabContent}>
        <Tests familyId={familyId} />
      </TabContent>
      <TabContent display={tab === 'settings'} className={classes.tabContent}>
        <FamilySettings family={family} />
      </TabContent>
    </div>
  ) : <LoadingIndicator loading={true} />;
};

export default FamilyPage;
